
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ProductDescription } from "@/lib/api";
import { useAuth } from "@/components/auth/AuthProvider";
import { useEffect } from "react";

export const useProductDescriptionQuery = (collectionId?: string) => {
  const { organization } = useAuth();
  
  // Check if we just navigated from generation (fresh collection)
  const urlParams = new URLSearchParams(window.location.search);
  const isJustGenerated = urlParams.has('generated') || urlParams.has('refreshed');
  
  const { data: descriptions, isLoading, refetch } = useQuery({
    queryKey: ["descriptions", collectionId, organization?.id],
    queryFn: async () => {
      try {
        console.log(`[Enhanced Query] Fetching descriptions for collection: ${collectionId}`);
        let query = supabase.from("product_descriptions").select("*");
        if (collectionId) {
          query = query
            .eq("collection_id", collectionId)
            .order("created_at", { ascending: true })
            .order("id", { ascending: true });
        } else if (organization?.id) {
          query = supabase
            .from("product_descriptions")
            .select(`
              *,
              collections!inner (
                organization_id
              )
            `)
            .eq("collections.organization_id", organization.id)
            .order("created_at", { ascending: true })
            .order("id", { ascending: true });
        }

        const { data, error } = await query;
        if (error) {
          console.error("Error fetching product descriptions:", error);
          throw error;
        }
        
        console.log(`[Enhanced Query] Successfully fetched ${data?.length || 0} descriptions`);
        
        return (data || []) as ProductDescription[];
      } catch (error) {
        console.error("Error in useProductDescriptions:", error);
        throw error;
      }
    },
    enabled: Boolean(collectionId || organization?.id),
    // Reduce stale time for fresh collections to ensure immediate refresh
    staleTime: isJustGenerated ? 0 : 15_000,
    gcTime: isJustGenerated ? 0 : 5 * 60 * 1000, // 5 minutes normally, 0 for fresh
    refetchOnWindowFocus: isJustGenerated, // Auto-refetch for fresh collections
    refetchOnMount: isJustGenerated ? 'always' : true,
  });

  // Enhanced realtime updates with retry logic
  useEffect(() => {
    if (!collectionId) return;

    console.log(`[Enhanced Realtime] Setting up channel for collection: ${collectionId}`);
    
    let retryCount = 0;
    const maxRetries = 3;
    
    const setupChannel = () => {
      const channel = supabase
        .channel(`product-descriptions-${collectionId}-${Date.now()}`, {
          config: {
            broadcast: { self: false },
            presence: { key: collectionId }
          }
        })
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'product_descriptions',
            filter: `collection_id=eq.${collectionId}`
          },
          (payload) => {
            console.log('[Enhanced Realtime] Database change detected:', payload.eventType);
            
            // Add retry logic for refetch
            const attemptRefetch = async (attempt = 1) => {
              try {
                await refetch();
                console.log('[Enhanced Realtime] Refetch successful');
              } catch (error) {
                console.error(`[Enhanced Realtime] Refetch attempt ${attempt} failed:`, error);
                if (attempt < 3) {
                  setTimeout(() => attemptRefetch(attempt + 1), 1000 * attempt);
                }
              }
            };
            
            attemptRefetch();
          }
        )
        .subscribe((status) => {
          console.log(`[Enhanced Realtime] Subscription status: ${status}`);
          
          if (status === 'CHANNEL_ERROR' && retryCount < maxRetries) {
            retryCount++;
            console.log(`[Enhanced Realtime] Retrying subscription (${retryCount}/${maxRetries})`);
            setTimeout(setupChannel, 2000 * retryCount);
          }
        });

      return channel;
    };

    const channel = setupChannel();

    return () => {
      console.log(`[Enhanced Realtime] Cleaning up channel for collection: ${collectionId}`);
      supabase.removeChannel(channel);
    };
  }, [collectionId, refetch]);

  // Auto-refetch for fresh collections with exponential backoff
  useEffect(() => {
    if (!isJustGenerated || !collectionId || isLoading) return;
    
    console.log('[Enhanced Query] Setting up auto-refetch for fresh collection');
    
    let retryCount = 0;
    const maxRetries = 5;
    
    const attemptRefetch = async () => {
      try {
        const result = await refetch();
        const hasData = result.data && result.data.length > 0;
        
        console.log(`[Enhanced Query] Refetch attempt ${retryCount + 1}: ${result.data?.length || 0} descriptions found`);
        
        if (!hasData && retryCount < maxRetries) {
          retryCount++;
          const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 8000); // Max 8s delay
          console.log(`[Enhanced Query] No data yet, retrying in ${delay}ms`);
          setTimeout(attemptRefetch, delay);
        }
      } catch (error) {
        console.error('[Enhanced Query] Refetch failed:', error);
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(attemptRefetch, 2000 * retryCount);
        }
      }
    };
    
    // Initial refetch after a short delay
    const timeoutId = setTimeout(attemptRefetch, 500);
    
    return () => clearTimeout(timeoutId);
  }, [isJustGenerated, collectionId, isLoading, refetch]);

  return {
    descriptions: descriptions || [],
    isLoading,
    refetch,
    isJustGenerated
  };
};
